import { useQuery } from '@tanstack/react-query';
import { apiClient, ApiError } from '@discovr/common';
import type { ImportResult } from '@discovr/types';
import { campaignKeys } from '../queryKeys';

/**
 * Fetches the stored import result for a campaign.
 * Returns null when no import has been run yet (BE returns 204), or when the
 * caller's role isn't permitted to view import results (BE returns 403) — in
 * both cases there's simply no import banner to show.
 */
export function useLastImportResult(
  campaignId: string,
  options?: { enabled?: boolean },
) {
  return useQuery<ImportResult | null>({
    queryKey: campaignKeys.lastImport(campaignId),
    queryFn: async () => {
      try {
        const res = await apiClient.get<{ data: ImportResult }>(
          `/campaigns/${encodeURIComponent(campaignId)}/last-import-result`,
        );
        // BE returns 204 (no body) when no import has been run yet.
        // Axios resolves 204 with res.data = '' — treat as null.
        if (res.status === 204 || !res.data?.data) return null;
        return res.data.data;
      } catch (err) {
        // 403 = this role can't see import results — degrade to "no import".
        if (err instanceof ApiError && err.httpStatus === 403) return null;
        throw err;
      }
    },
    // Import results are an admin-app-only capability: the endpoint requires the
    // roster:import scope, so brand callers only ever get a 403 (which we swallow
    // below). Callers pass enabled=false for roles that can't import so the brand
    // app never fires the guaranteed-403 request at all. Defaults to true to keep
    // existing callers unchanged.
    enabled: !!campaignId && (options?.enabled ?? true),
    staleTime: 5 * 60_000, // 5 min — result changes only on new upload
  });
}
